import {
    IResponseData,
    IMagicLinkResponseData,
    IOrderModalAppShellPayLoad,
    IOrderModalMagicLinkPayLoad,
} from './types';

export class HttpError extends Error {
    status: number;
    response: Response;

    constructor(response: Response) {
        super(`HTTP error ${response.status}`);
        this.status = response.status;
        this.response = response;
    }
}

class Fetcher {
    public readonly magicLink: string;
    private readonly loggingEnabled: boolean;
    private readonly isMobile: boolean;
    private readonly apiUrl: string;
    private readonly magicLinkApiUrl: string;

    constructor(magicLink: string, enabledClientLogging?: boolean, isMobile?: boolean) {
        this.magicLink = magicLink;

        this.loggingEnabled = enabledClientLogging;
        this.isMobile = isMobile;
        this.apiUrl = this.magicLink + 'api/ordermodal/OrderModalData';
        this.magicLinkApiUrl = this.magicLink + 'api/ordermodal/OrderModalDataWithMagicLink';
        this.handleError.bind(this);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private isHttpError(error: any): error is HttpError {
        return error instanceof HttpError;
    }

    private async fetchJson<T>(url: string, timeoutMs = 5000): Promise<T> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new HttpError(response);
            return response.json() as Promise<T>;
        } finally {
            clearTimeout(timer);
        }
    }

    

    private handleError(error: Error | HttpError) {
        if (this.loggingEnabled) {
            const errorMsg = this.isHttpError(error) ? error.message : `${error.name}: ${error.message}`;
            const errorStack = this.isHttpError(error) ? `status: ${error.status}` : error.stack;
            // eslint-disable-next-line no-console
            console.warn(`ajax fetch failed with "${errorMsg}". Full error:\n${errorStack}`);
        }
        return error;
    }

    public getOrderModalData(
        appShellPayload: IOrderModalAppShellPayLoad,
        onSuccess?: (data: IResponseData) => void,
        onFailure?: (error: Error | HttpError) => void,
        onFinally?: () => void
    ): void {
        const requestUrl = new URL(this.apiUrl);
        requestUrl.searchParams.set('salesArticleNo', appShellPayload.salesArticleNo);
        requestUrl.searchParams.set('masterArticleNo', String(appShellPayload.masterArticleNo));
        requestUrl.searchParams.set('colorCode', String(appShellPayload.colorCode));
        if (appShellPayload.sizeCode) requestUrl.searchParams.set('sizeCode', String(appShellPayload.sizeCode));

        this.fetchJson(requestUrl.toString(), 5000)
            .then((responseData: IResponseData) => {
                typeof onSuccess === 'function' && onSuccess(responseData);
                return responseData;
            })
            .catch((error: Error | HttpError) => {
                const result = this.handleError(error);
                typeof onFailure === 'function' && onFailure(error);
                return result;
            })
            .finally(() => {
                typeof onFinally === 'function' && onFinally();
            });
    }

    public getOrderModalDataViaMagicLink(
        appShellPayload: IOrderModalMagicLinkPayLoad,
        onSuccess?: (data: IMagicLinkResponseData) => void,
        onFailure?: (error: Error | HttpError) => void,
        onFinally?: () => void
    ): void {
        const requestUrl = new URL(this.magicLinkApiUrl);
        requestUrl.searchParams.set('magicLink', appShellPayload.magicLink);

        this.fetchJson(requestUrl.toString(), 5000)
            .then((responseData: IResponseData) => {
                typeof onSuccess === 'function' && onSuccess(responseData);
                return responseData;
            })
            .catch((error: Error | HttpError) => {
                const result = this.handleError(error);
                typeof onFailure === 'function' && onFailure(error);
                return result;
            })
            .finally(() => {
                typeof onFinally === 'function' && onFinally();
            });
    }

    setPriceModeCookie(newPriceMode: 'Net' | 'Gross') {
        const urlToHost = this.magicLink.replace('api/', '');
        const apiUrl = urlToHost + `${this.isMobile ? 'majax' : 'ajax'}/update/Content/Empty2`;
        const requestURL = new URL(apiUrl);

        requestURL.searchParams.set('PriceMode', newPriceMode);
        this.fetchJson(requestURL.toString())
            .then(() => {
                if (typeof window !== 'undefined') {
                    window.shell.publishTo('PriceModeChanged', newPriceMode);
                }
            })
            .catch((error: Error | HttpError) => {
                return this.handleError(error);
            });
    }
}

export default Fetcher;
